import { OrganizationSubscription, SubscribedProduct, User } from "types";

interface RedirectRule<T> {
  condition: (arg: T) => boolean;
  redirectTo: string;
  enabled?: boolean;
  priority?: number; // The strictest rule has the highest priority
  features?: string[]; // Controls which features have to be enabled to apply the rule
  excludedPages?: string[]; // Pages where the rule should not be applied
}

export interface RedirectContext {
  user: User;
  subscriptions?: SubscribedProduct[];
  organizationSubscription?: OrganizationSubscription | null;
  paywallFlow?: string;
  isOrgWhitelisted?: boolean;
  hasCreatorProfile?: boolean;
}

export interface RedirectRules<T> {
  rules: Record<string, RedirectRule<T>>;
  sorted?: RedirectRule<T>[];
}

const MAX_PRIORITY = 100;
const REDIRECT_RULES: RedirectRules<RedirectContext> = {
  rules: {
    goToOrganizationCreate: {
      condition: ({ user }) => user.organizations?.length === 0,
      redirectTo: "/organization/create",
      priority: MAX_PRIORITY,
      excludedPages: [
        "/onboarding",
        "/organization/create",
        "/register",
        "/login",
        "/verification",
      ],
    },
    goToPaywall: {
      condition: ({ user, organizationSubscription, paywallFlow }) => {
        const isFree =
          !organizationSubscription ||
          organizationSubscription.license_type === "Free";
        if (!isFree) return false;
        const PAYWALL_CUTOFF = "2026-04-17";
        const isNewUser = !!user.created && user.created >= PAYWALL_CUTOFF;
        if (!isNewUser) return false;

        if (paywallFlow === "soft_3_exp" || paywallFlow === "hard_no_card_3d") {
          // These groups use soft paywall modals instead of hard redirect
          return false;
        }

        // control: redirect immediately
        return true;
      },
      redirectTo: "/paywall",
      priority: MAX_PRIORITY - 10,
      excludedPages: [
        "/paywall",
        "/login",
        "/register",
        "/forgot-password",
        "/verification",
        "/organization",
        "/organization/create",
        "/onboarding",
        "/settings",
      ],
    },
    goToTeacherOnboarding: {
      condition: ({ isOrgWhitelisted, hasCreatorProfile }) =>
        !!isOrgWhitelisted && !hasCreatorProfile,
      redirectTo: "/creator-profile",
      priority: MAX_PRIORITY - 20,
      excludedPages: [
        "/creator-profile",
        "/login",
        "/register",
        "/organization/create",
        "/verification",
      ],
    },
  },
};

const sorted = sortRulesByPriority(REDIRECT_RULES.rules);
REDIRECT_RULES.sorted = sorted;

export function testRules(
  context: RedirectContext,
  enabledFeatures: string[],
  rules = REDIRECT_RULES.sorted ?? [],
  currentRoute = globalThis.window.location.pathname
) {
  // Verify the rules
  for (const rule of rules) {
    const isEnabled = rule.features
      ? rule.features.every((feature) => enabledFeatures.includes(feature))
      : true;
    if (!isEnabled) continue;
    // If the current route is the same as the redirectTo, skip this rule
    if (currentRoute === rule.redirectTo) return;
    // Check if we're on an excluded page
    if (rule.excludedPages?.includes(currentRoute)) continue;
    const shouldRedirect = rule.condition(context);

    if (shouldRedirect) {
      return rule.redirectTo;
    }
  }
}

export function sortRulesByPriority<T>(
  rules: Record<string, RedirectRule<T>>,
  order: "asc" | "desc" = "desc"
) {
  const copy = Object.values(rules);
  if (order === "asc") {
    copy.sort(
      (a, b) => (a?.priority ?? -Infinity) - (b?.priority ?? -Infinity)
    );
  } else {
    copy.sort(
      (a, b) => (b?.priority ?? -Infinity) - (a?.priority ?? -Infinity)
    );
  }
  return copy;
}
